import AppForm from '@/components/AppForm';
import { SuccessResponseModal } from '@/components/AppModal/modals';
import AppPageTitle from '@/components/AppPageTitle';
import AppSelect from '@/components/AppSelect';
import FormUpload from '@/components/common/FormUpload';
import PATH from '@/config/paths/PATH';
import { useUnsaved } from '@/contexts/UnsavedContext';
import useFormSubmit from '@/hooks/useFormSubmit';
import { createPrizeManagement } from '@/services/prize_management.service';
import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Input, InputNumber, Modal, Row, Space, Switch, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PieceTable from './PieceTable';
import RowAction from './RowAction';
import usePrizeManagement from '@/redux/hooks/usePrizeManagement';
import { formatNumber } from '@/utils/formatters';
import { v4 as uuidv4 } from 'uuid';
import { CloseOutlined } from '@ant-design/icons';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().endOf('month').format('YYYY-MM-DD');

function PrizeManagementCreate() {
  // essentials
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // states
  const [pieceList, setPieceList] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { setHasUnsaved } = useUnsaved();
  const { setPieceValue } = usePrizeManagement();

  // api call
  const createPrizeMutation = useMutation({
    mutationFn: createPrizeManagement,
  });

  // handle remove
  const handleRemovePiece = (item) => {
    Modal.confirm({
      title: 'Delete piece',
      content: `Are you sure you want to delete the piece "${item.name}"?`,
      onOk: () => {
        if (pieceList.length === 1) {
          setPieceList([]);
          return;
        }
        setPieceList((prev) => prev.filter((i) => i.id !== item.id));
      },
    });
  };

  const handleRemoveSelectedPieces = () => {
    if (selectedRowKeys.length === 0) {
      Modal.warning({
        title: 'No pieces selected',
        content: 'Please select at least one piece to delete.',
      });
      return;
    }

    Modal.confirm({
      title: 'Delete selected piece(s)',
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected piece(s)?`,
      onOk: () => {
        setPieceList((prev) => prev.filter((item) => !selectedRowKeys.includes(item.id)));
        setSelectedRowKeys([]);
      },
    });
  };
  const onSubmit = useFormSubmit(
    createPrizeMutation,
    async (values) => {
      const imageFile = values.image?.[0]?.originFileObj || null;

      const { image, isdn_win, isdn_block, ...restValues } = values;
      const piecesData = pieceList.map((item, index) => {
        const { image_file, ...pieceData } = item;
        const originalFileName = image_file?.name || 'unnamed';

        const fileExtension = originalFileName.includes('.')
          ? originalFileName.split('.').pop()
          : 'jpg';
        const baseFileName = originalFileName.includes('.')
          ? originalFileName.replace(/\.[^/.]+$/, '')
          : originalFileName;
        const newFileName = `${baseFileName}_piece_${item.id}.${fileExtension}`;
        return {
          ...pieceData,
          is_active: item.is_active ? 1 : 0,
          is_limited: item.is_limited ? 1 : 0,
          piece_image_id: newFileName,
        };
      });

      const payload = {
        ...restValues,
        is_active: values.is_active ? 1 : 2,
        piece: piecesData,
        isdn_win: isdn_win ? isdn_win.replace(/\s+/g, '') : '',
        isdn_block: isdn_block ? isdn_block.replace(/\s+/g, '') : '',
      };

      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        if (payload[key] !== null && payload[key] !== undefined) {
          if (typeof payload[key] === 'object') {
            formData.append(key, JSON.stringify(payload[key]));
          } else {
            formData.append(key, payload[key]);
          }
        }
      });

      if (imageFile) {
        formData.append('image', imageFile);
      }

      pieceList.forEach((item, index) => {
        if (item.image_file) {
          const originalFileName = item.image_file?.name || 'unnamed';

          const fileExtension = originalFileName.includes('.')
            ? originalFileName.split('.').pop()
            : 'jpg';
          const baseFileName = originalFileName.includes('.')
            ? originalFileName.replace(/\.[^/.]+$/, '')
            : originalFileName;

          const newFileName = `${baseFileName}_piece_${item.id}.${fileExtension}`;

          formData.append('piece_image', item.image_file, newFileName);
        }
      });

      return formData;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            navigate(PATH.PRIZE_MANAGEMENT.ROOT, {
              replace: true,
            });
          },
        });
      },
    }
  );

  useEffect(() => {
    if (pieceList.length > 0) {
      setHasUnsaved(true);
    }
  }, [pieceList, setHasUnsaved]);

  useEffect(() => {
    return () => {
      setPieceList([]);
    };
  }, []);

  const isdnWinValue = Form.useWatch('isdn_win', form);
  const isdnBlockValue = Form.useWatch('isdn_block', form);

  const phoneNumberWins = useMemo(() => {
    if (!isdnWinValue) return [];
    return isdnWinValue
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
  }, [isdnWinValue]);

  const phoneNumberBlocks = useMemo(() => {
    if (!isdnBlockValue) return [];
    return isdnBlockValue
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
  }, [isdnBlockValue]);

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  // render
  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Create Prize" />

      <AppForm
        form={form}
        onSubmit={onSubmit}
        targetPath={PATH.PRIZE_MANAGEMENT.ROOT}
        buttonProps={{
          loading: createPrizeMutation.isPending,
          disabled: createPrizeMutation.isPending,
        }}
        disabled={createPrizeMutation.isPending}
      >
        <Row gutter={24}>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item
              label="Prize Name"
              name="name"
              rules={[{ message: 'Please prize name', required: true }]}
            >
              <Input placeholder="Enter prize name" />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[{ message: 'Please enter prize description', required: true }]}
            >
              <Input placeholder="Enter prize description" />
            </Form.Item>
            <Form.Item
              label="Price (MMK)"
              name="price"
              rules={[{ message: 'Please enter price', required: true }]}
            >
              <InputNumber
                placeholder="Enter price"
                min={0}
                formatter={(value) => formatNumber(value)}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label="Period"
              name="period"
              rules={[{ required: true, message: 'Please select period' }]}
              initialValue={{ start_date: START_DATE, end_date: END_DATE }}
            >
              <Row gutter={8} className="flex-nowrap justify-between">
                <Col span={11}>
                  <Form.Item
                    name="start_date"
                    noStyle
                    getValueFromEvent={(value) => value?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({
                      value: value ? dayjs(value) : undefined,
                    })}
                    rules={[
                      {
                        required: true,
                        message: 'Please select start date',
                      },
                    ]}
                    initialValue={START_DATE}
                  >
                    <DatePicker
                      placeholder="Start Date"
                      format="YYYY-MM-DD"
                      className="w-full"
                      onChange={() => {
                        form.validateFields(['end_date']);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col className="text-center flex items-center">
                  <span className="text-gray-500">to</span>
                </Col>
                <Col span={11}>
                  <Form.Item
                    name="end_date"
                    noStyle
                    getValueFromEvent={(value) => value?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({
                      value: value ? dayjs(value) : undefined,
                    })}
                    rules={[
                      { required: true, message: 'Please select end date' },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();

                          const startDate = form.getFieldValue('start_date');
                          if (!startDate) return Promise.resolve();

                          const start = dayjs(startDate);
                          const end = dayjs(value);

                          if (end.isBefore(start, 'd')) {
                            return Promise.reject(
                              new Error('End date cannot be before start date')
                            );
                          }

                          if (end.isSame(start, 'd')) {
                            return Promise.reject(new Error('End date must be after start date'));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    initialValue={END_DATE}
                  >
                    <DatePicker
                      placeholder="End Date"
                      format="YYYY-MM-DD"
                      className="w-full"
                      disabledDate={(current) => {
                        const startDate = form.getFieldValue('start_date');
                        const currentDate = dayjs(current);
                        if (!startDate) return false;

                        return (
                          current &&
                          (currentDate.isSame(dayjs(startDate), 'd') ||
                            currentDate.isBefore(dayjs(startDate), 'd'))
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              label="Image"
              name="image"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
              rules={[{ message: 'Please upload image', required: true }]}
              initialValue={[]}
            >
              <FormUpload className="w-[300px]" beforeUpload={() => false} />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item
              label="Prize Number"
              name="num_prize"
              rules={[{ message: 'Please enter prize number', required: true }]}
            >
              <InputNumber
                className="w-full"
                placeholder="Enter prize number"
                min={0}
                formatter={(value) => formatNumber(value)}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
            <Form.Item
              label="Prize Type"
              name="type"
              rules={[{ message: 'Please select type', required: true }]}
              initialValue={1}
            >
              <AppSelect
                items={[
                  { value: 1, label: 'Topup' },
                  { value: 2, label: 'Physical' },
                ]}
                placeholder="Select prize type"
                formItemClassName={'mb-0'}
              />
            </Form.Item>
            <Form.Item
              label="Percentage"
              name="percent"
              rules={[{ message: 'Please enter percentage', required: true }]}
            >
              <Input
                type="number"
                addonBefore="%"
                className="w-full"
                placeholder="Enter percentage"
                min={0}
                max={100}
              />
            </Form.Item>
            <Form.Item
              label="Piece Per Row"
              name="piece_per_row"
              rules={[{ message: 'Please enter piece per row', required: true }]}
            >
              <Input min={1} type="number" placeholder="Enter piece Per Row" />
            </Form.Item>
            <Form.Item
              label="ISDN Win"
              name="isdn_win"
              rules={[
                // { message: 'Please enter ISDN Win', required: true },
                {
                  pattern: /^(\+?\d+(,\s*\+?\d+)*)?$/,
                  message: 'Only numbers separated by commas (with optional spaces) are allowed',
                },
              ]}
            >
              <Input
                placeholder="Enter ISDN Win"
                type="tel"
                onBeforeInput={(e) => {
                  // allow only digits, +, comma, and space
                  if (!/[0-9,+ ]/.test(e.data)) {
                    e.preventDefault();
                  }
                }}
                onKeyDown={(e) => {
                  // allow navigation & meta shortcuts
                  if (
                    e.ctrlKey ||
                    e.metaKey ||
                    ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                  ) {
                    return;
                  }

                  // block other unwanted keys
                  if (!/[0-9,+ ]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
            {phoneNumberWins?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {phoneNumberWins?.map((num, idx) => (
                  <Tag
                    key={`${num}-${uuidv4()}`} // Stable key: use the phone number itself (unique)
                    closable
                    onClose={() => {
                      const newValue = phoneNumberWins
                        ?.filter((_, i) => i !== idx) // Filter by index (stable with good keys)
                        ?.join(', ');
                      form.setFieldValue('isdn_win', newValue);
                      setHasUnsaved(true);
                    }}
                    closeIcon={<CloseOutlined />}
                    color="blue"
                  >
                    {num}
                  </Tag>
                ))}
              </div>
            )}
            <Form.Item
              label="ISDN Block"
              name="isdn_block"
              rules={[
                // { message: 'Please enter ISDN Block', required: true },
                {
                  pattern: /^(\+?\d+(,\s*\+?\d+)*)?$/,
                  message: 'Only numbers separated by commas (with optional spaces) are allowed',
                },
              ]}
            >
              <Input
                placeholder="Enter ISDN Block"
                type="tel"
                onBeforeInput={(e) => {
                  // allow only digits, +, comma, and space
                  if (!/[0-9,+ ]/.test(e.data)) {
                    e.preventDefault();
                  }
                }}
                onKeyDown={(e) => {
                  // allow navigation & meta shortcuts
                  if (
                    e.ctrlKey ||
                    e.metaKey ||
                    ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                  ) {
                    return;
                  }

                  // block other unwanted keys
                  if (!/[0-9,+ ]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
            {phoneNumberBlocks?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {phoneNumberBlocks?.map((num, idx) => (
                  <Tag
                    key={`${num}-${uuidv4()}`} // Stable key: use the phone number itself (unique)
                    closable
                    onClose={() => {
                      const newValue = phoneNumberBlocks
                        ?.filter((_, i) => i !== idx) // Filter by index (stable with good keys)
                        ?.join(', ');
                      form.setFieldValue('isdn_block', newValue);
                      setHasUnsaved(true);
                    }}
                    closeIcon={<CloseOutlined />}
                    color="blue"
                  >
                    {num}
                  </Tag>
                ))}
              </div>
            )}
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <PieceTable
          pieceList={pieceList}
          setPieceList={setPieceList}
          hasRowSelect
          rowKey="id"
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          onCreate={pieceList.length > 0 ? () => setOpenCreate(true) : null}
          onRemoveItems={handleRemoveSelectedPieces}
          dataSource={pieceList.filter((item) =>
            Object.keys(item).some(
              (key) =>
                key !== 'id' && item[key] !== undefined && item[key] !== '' && item[key] !== null
            )
          )}
          onOpenCreate={() => {
            setOpenCreate(true);
          }}
          openCreate={openCreate}
          openUpdate={openUpdate}
          setOpenCreate={setOpenCreate}
          setOpenUpdate={setOpenUpdate}
          isNewPiece={true}
          rowAction={(record) => (
            <RowAction
              record={record}
              onEdit={() => {
                setPieceValue(record);
                setOpenUpdate(true);
              }}
              onDelete={() => handleRemovePiece(record)}
            />
          )}
        />
      </AppForm>
    </Space>
  );
}

export default PrizeManagementCreate;
